import { z } from 'zod'

/**
 * CRM shared types: workstream C (field filters, sorting, saved views, permissions).
 *
 * Filters name a field the way the list screen does: standard columns by name (`name`, `created_at`,
 * `organisation`, ...) and the object's own fields as `data.<key>` (custom records) or `fields.<key>` (custom
 * fields on people and organisations). The server checks every field and operator against the object's
 * schema and what the viewer may see (services/crmViews.ts).
 */
export const FILTER_OPS = [
  'eq', 'neq', 'contains', 'in', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'not_empty', 'within_next_days', 'within_last_days',
] as const
export const FilterOp = z.enum(FILTER_OPS)
export type FilterOp = z.infer<typeof FilterOp>

const Scalar = z.union([z.string().max(500), z.number(), z.boolean()])
export const ViewFilter = z.object({
  field: z.string().min(1).max(80),
  op: FilterOp,
  /** A scalar; a list for `in`; [from, to] for `between`; a number of days for `within_*`; nothing for `is_empty`/`not_empty`. */
  value: z.union([Scalar, z.array(Scalar).max(50)]).nullish(),
})
export type ViewFilter = z.infer<typeof ViewFilter>

export const ViewSort = z.object({ field: z.string().min(1).max(80), dir: z.enum(['asc', 'desc']).default('desc') })
export type ViewSort = z.infer<typeof ViewSort>

/** POST /crm/list/:object: one page of a filtered, sorted list. */
export const ListQuery = z.object({
  filters: z.array(ViewFilter).max(20).default([]),
  sort: ViewSort.nullish(),
  q: z.string().max(200).nullish(),
  cursor: z.string().max(2000).nullish(),
  limit: z.number().int().min(1).max(100).default(50),
})
export type ListQuery = z.infer<typeof ListQuery>

/** How a list field compares, and so which operators it offers. */
export type ViewFieldKind = 'text' | 'number' | 'boolean' | 'list' | 'date' | 'datetime' | 'ref'
export interface ViewField {
  key: string; label: string; kind: ViewFieldKind; standard: boolean
  options?: string[]; ops: FilterOp[]; sortable: boolean
  /** A pii field the viewer may not see: shown as hidden, and never filtered or sorted on. */
  masked?: boolean
}

export const SavedViewInput = z.object({
  name: z.string().trim().min(1).max(80),
  object: z.string().min(1).max(63),
  filters: z.array(ViewFilter).max(20).default([]),
  sort: ViewSort.nullish(),
  columns: z.array(z.string().min(1).max(80)).max(12).nullish(),
  /** Shared with everyone who may read the object; otherwise only its owner sees it. */
  shared: z.boolean().default(false),
})
export type SavedViewInput = z.infer<typeof SavedViewInput>

export interface SavedView {
  id: string; name: string; object: string
  filters: ViewFilter[]; sort: ViewSort | null; columns: string[] | null
  shared: boolean; owner: { id: string; name: string } | null
  /** The viewer created it, or is an admin or supervisor, so may change or delete it. */
  can_edit: boolean
  created_at: string; updated_at: string
}
